import React from 'react';
import CommonTable from '../../Components/CommonTable';
import StatusBadge from '../../Components/StatusBadge';
import NoDataPage from '../../Components/NoDataPage';
import ModuleIcon from '../../Components/ModuleIcon';
import VendorAvatar from '../../Components/VendorAvatar';
import { Body, SubHeading } from '../../Constants/Fonts';
import { formatDate } from '../../TpcrmPortal/tpcrmUtils';

// Highest Risk Vendors — the same monitored-vendor data that fed the grade
// distribution, re-presented as the ranked table from the reference design.
// Rows are already ordered worst-first by the parent. Risk score / level are
// derived from each vendor's grade (see parent's mapping) so this stays in
// sync with the rest of the widget rather than inventing a second source.

const LEVEL_TO_STATUS = { Low: 'low', Medium: 'medium', High: 'high', Critical: 'critical' };

// First non-empty date field a vendor record tends to carry for "last assessed".
const lastAssessed = (v) => {
    const raw = v?.last_assessed
        || v?.latest_assessment?.completed_at
        || v?.latest_assessment?.submitted_at
        || v?.latest_assessment?.created_at
        || v?.updated_at;
    return raw ? formatDate(raw) : '-';
};

function HighestRiskVendors({
    vendors = [],
    onViewAll,
    onRowClick,
    bare = false,
    // Reuse this same rich table under a different label (e.g. the portal's
    // "Best performing" / "Worst performing" standings). `showHeader={false}`
    // drops the built-in title row entirely when the caller already renders one.
    title = 'Highest Risk Vendors',
    icon = 'tpcrm',
    // ModuleIcon paints via an SVG `stroke` attribute, which does NOT resolve
    // `var(--…)` — pass a concrete hex (e.g. the sidebar navy) so the glyph
    // actually takes the theme colour. Defaults to the brand navy token.
    iconColor = '#1e3c65',
    showHeader = true,
    emptyTitle = 'No graded vendors yet',
    emptyHint = 'Vendors appear here once they carry a grade.',
}) {
    // Only surface the Open Findings column when the API actually returned a
    // count for at least one vendor — otherwise it's a column of dashes.
    const showFindings = vendors.some((v) => v.openFindings != null);

    const HEADERS = [
        'Vendor',
        'Risk Score',
        'Risk Level',
        ...(showFindings ? ['Open Findings'] : []),
        'Business Criticality',
        'Last Assessed',
        '',
    ];

    const rows = vendors.map((v) => ({
        rowStyle: onRowClick ? { cursor: 'pointer' } : undefined,
        onClick: onRowClick ? () => onRowClick(v) : undefined,
        data: [
            // Vendor — avatar + name (bold) over domain (muted), stacked.
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <VendorAvatar label={v.name || v.domain} />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                    <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">
                        {v.name || v.domain || '-'}
                    </Body>
                    {v.domain && v.name && (
                        <Body as="span" variant={3} color="var(--color-text-muted)">
                            {v.domain}
                        </Body>
                    )}
                </span>
            </div>,

            // Risk Score — "48 / 100"
            <span>
                <Body as="span" variant={2} weight="semibold" color="var(--color-text-primary)">
                    {v.riskScore}
                </Body>
                <Body as="span" variant={2} color="var(--color-text-muted)"> / 100</Body>
            </span>,

            // Risk Level pill
            <StatusBadge status={LEVEL_TO_STATUS[v.riskLevel] || 'neutral'} label={v.riskLevel} />,

            // Open Findings — only present when the column is shown.
            ...(showFindings ? [
                <Body as="span" variant={2} color="var(--color-text-secondary)">
                    {v.openFindings != null ? v.openFindings : '-'}
                </Body>,
            ] : []),

            <Body as="span" variant={2} color="var(--color-text-secondary)">
                {v.criticality || '-'}
            </Body>,

            <Body as="span" variant={2} color="var(--color-text-secondary)">
                {lastAssessed(v.raw)}
            </Body>,

            // Chevron affordance
            onRowClick ? (
                <Body as="span" variant={2} color="var(--color-text-muted)" style={{ fontSize: 16 }}>›</Body>
            ) : '',
        ],
    }));

    const inner = (
        <>
            {showHeader && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: bare ? 12 : 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {icon && <ModuleIcon name={icon} size={22} color={iconColor} />}
                        <SubHeading as="h3" style={{ margin: 0 }} color="var(--color-text-primary)">
                            {title}
                        </SubHeading>
                    </div>
                    {onViewAll && (
                        <Body
                            as="button"
                            variant={2}
                            weight="semibold"
                            color="var(--color-primary)"
                            onClick={onViewAll}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                            View All Vendors →
                        </Body>
                    )}
                </div>
            )}

            {rows.length === 0 ? (
                <NoDataPage
                    title={emptyTitle}
                    hint={emptyHint}
                    minHeight={120}
                />
            ) : (
                <div className="hrv_compact_table" style={{ overflowX: 'auto' }}>
                    <CommonTable headers={HEADERS} rows={rows} onRowClick={(row) => row?.onClick?.()} />
                </div>
            )}
        </>
    );

    // `bare` → render just the header + table (no card), for nesting inside an
    // existing panel that already provides the card surface.
    if (bare) return inner;

    return <div className="cmn_elevated_card" style={{ padding: 24 }}>{inner}</div>;
}

export default HighestRiskVendors;
